/* survivalAnalysisThunk.ts */

/* Redux-Toolkit */
import { createAsyncThunk } from "@reduxjs/toolkit";

/* Axios */
import axios, { HttpStatusCode } from "axios";

/* Utils */
import axiosInterceptor from "src/utils/helper/axios-interceptor";
import { InsightsFilterRequestDto } from "src/utils/dto/insights-filter/InsightsFilterRequestDto";
import { BaseChartResponseDto } from "src/utils/dto/charts/BaseChartResponseDto";
import { transformSurvivalAnalysisData } from "src/utils/helper/survival-analysis/transformSurvivalAnalysisData";
import { ClientError } from "src/utils/status-handling/ClientError";

/* Constants */
import { ErrorMessage } from "src/shared/constants/common/ErrorMessage";

/* Constants */
import { StartPointType } from "src/shared/constants/survival-analysis/StartPoint";
import { ChartType } from "src/shared/constants/charts/Chart";

/* Interfaces */
import { BaseChartProps } from "src/shared/interfaces/charts/BaseChart";
import { SurvivalAnalysisProps } from "src/shared/interfaces/SurvivalAnalysis";
import { SurvivalAnalysisFilterRequest } from "src/shared/interfaces/api/InsightsFilters";

export const fetchSurvivalAnalysis = createAsyncThunk<
  BaseChartProps<SurvivalAnalysisProps, ChartType[]>,
  SurvivalAnalysisFilterRequest,
  {
    rejectValue: ClientError;
  }
>(
  "survivalAnalysis/fetchSurvivalAnalysis",
  async (
    params: SurvivalAnalysisFilterRequest,
    { rejectWithValue }
  ) => {
    try {
      const requestParams =
        InsightsFilterRequestDto.toSurvivalAnalysisRequest(
          params
        );

      const response = await axiosInterceptor.get(
        "/data/survival_data",
        {
          // Always fetch the no-value cohort groups; the `showNoValueEntries`
          // toggle only controls client-side display (see descriptive stats).
          params: {
            ...requestParams,
            include_no_value_states: true,
          },
          paramsSerializer: { indexes: null },
        }
      );

      if (
        response.status === HttpStatusCode.NoContent ||
        !response.data
      ) {
        return rejectWithValue(ClientError.noContent());
      }

      const dto = BaseChartResponseDto.fromApi(
        response.data,
        transformSurvivalAnalysisData
      );

      const result = dto.toObject();
      return result;
    } catch (error) {
      if (
        axios.isAxiosError(error) &&
        error.response?.status === HttpStatusCode.Forbidden
      ) {
        return rejectWithValue(ClientError.forbidden(ErrorMessage.NO_CARE_SITE_ACCESS));
      }
      if (error instanceof ClientError) {
        return rejectWithValue(error);
      }
      return rejectWithValue(ClientError.internal());
    }
  }
);

export const fetchStartPoints = createAsyncThunk(
  "survivalAnalysis/fetchStartPoints",
  async (): Promise<StartPointType[] | undefined> => {
    try {
      const response = await axiosInterceptor.get(
        "/options/survival_analysis_start_points"
      );
      const { data } = response;
      return data;
    } catch (error) {
      console.log(error);
    }
  }
);
